/**
 * Pending hiring intent
 * =====================================================
 * Carries the role/intent a visitor typed into the landing hero or footer CTA
 * across the sign-up → onboarding journey and into the Hiring Assistant.
 *
 * Deliberately single-consume and time-boxed: the value is written on the
 * landing page, read once when the "Pick Up Where You Left Off" modal is
 * shown, then cleared. The TTL guards against a value lingering on a shared
 * browser and resurfacing for an unrelated, much later session.
 */

const PENDING_ROLE_KEY = 'precision_pending_role';
// A genuine signup → onboarding → tour journey completes well within this
// window; anything older is treated as stale and ignored.
const PENDING_ROLE_TTL_MS = 72 * 60 * 60 * 1000; // 72 hours

interface StoredIntent {
  role: string;
  ts: number;
}

/**
 * Persist the role a visitor typed before sending them to sign up.
 * No-ops for empty input so we never trigger the modal with nothing to show.
 */
export function setPendingRole(role: string): void {
  const trimmed = role.trim();
  if (!trimmed) return;
  try {
    const payload: StoredIntent = { role: trimmed, ts: Date.now() };
    localStorage.setItem(PENDING_ROLE_KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable — silently skip the hand-off */
  }
}

function readValidIntent(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_ROLE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredIntent;
    if (!parsed?.role || typeof parsed.ts !== 'number') {
      clearPendingRole();
      return null;
    }
    if (Date.now() - parsed.ts > PENDING_ROLE_TTL_MS) {
      clearPendingRole();
      return null;
    }
    return parsed.role;
  } catch {
    clearPendingRole();
    return null;
  }
}

/** Read the pending role without removing it (used to decide whether to show the modal). */
export function peekPendingRole(): string | null {
  return readValidIntent();
}

/** Read the pending role and remove it (used when the user acts on the modal). */
export function consumePendingRole(): string | null {
  const role = readValidIntent();
  if (role) clearPendingRole();
  return role;
}

export function clearPendingRole(): void {
  try {
    localStorage.removeItem(PENDING_ROLE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Run a wholesale `localStorage` wipe without losing a carried-over intent.
 *
 * The sign-in page and the 401 interceptor both call `localStorage.clear()` to
 * drop a previous session. That also took `precision_pending_role` with it, so
 * a visitor who typed a role on the landing page and then *logged in* (rather
 * than signing up) arrived at the Hiring Assistant with nothing to pick up —
 * the sign-up route never wipes storage, which is why the hand-off only ever
 * worked there. Snapshot the raw payload (timestamp included, so the TTL keeps
 * counting from when it was typed) and put it back afterwards.
 */
export function preservePendingRole(wipe: () => void): void {
  let snapshot: string | null = null;
  try {
    snapshot = localStorage.getItem(PENDING_ROLE_KEY);
  } catch {
    /* storage unavailable — nothing to preserve */
  }

  wipe();

  if (!snapshot) return;
  try {
    localStorage.setItem(PENDING_ROLE_KEY, snapshot);
  } catch {
    /* ignore — the hand-off is best-effort */
  }
}
